import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Star, Eye, EyeOff, Download, Mail, Ban } from "lucide-react";
import DatePicker from "@/components/DatePicker";

export function ClosuresManager({ closures, refresh }) {
  const [date, setDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const add = async (e) => {
    e.preventDefault();
    if (!date) { toast.error("Scegli una data"); return; }
    if (endDate && endDate < date) { toast.error("La data di fine precede quella di inizio"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/closures", { date, end_date: endDate || null, reason });
      toast.success(data.created > 1 ? `${data.created} giorni segnati come chiusi` : "Giorno segnato come chiuso");
      setDate(""); setEndDate(""); setReason(""); await refresh();
    }
    catch { toast.error("Errore nel salvataggio"); }
    finally { setSaving(false); }
  };
  const del = async (id) => { await api.delete(`/closures/${id}`); toast.success("Chiusura rimossa"); await refresh(); };
  const fmt = (d) => new Date(d + "T00:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
  return (
    <div className="grid lg:grid-cols-5 gap-8 min-w-0">
      <form onSubmit={add} className="lg:col-span-2 min-w-0 space-y-4 p-5 sm:p-6 rounded-2xl border border-[#D4AF37]/15 bg-[#141217] h-fit">
        <h3 className="font-display text-xl text-[#FBF8F5]">Chiusura Straordinaria</h3>
        <p className="text-xs text-[#6E6865]">Il giorno apparirà barrato nel calendario e non sarà conteggiato per la prossima serata.</p>
        <div>
          <label className="text-xs text-[#A39E9B] mb-1 block">Dal giorno</label>
          <DatePicker value={date} onChange={setDate} testid="closure-date-input" />
        </div>
        <div>
          <label className="text-xs text-[#A39E9B] mb-1 block">Fino al giorno (facoltativo, per un periodo)</label>
          <DatePicker value={endDate} onChange={setEndDate} testid="closure-end-date-input" />
        </div>
        <div>
          <label className="text-xs text-[#A39E9B] mb-1 block">Motivo (facoltativo)</label>
          <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="closure-reason-input" placeholder="es. Evento privato" className={inputCls} />
        </div>
        <button type="submit" disabled={saving} data-testid="closure-add-btn"
          className="w-full flex items-center justify-center gap-2 py-3 rounded-full bg-[#7A1426] hover:bg-[#941B30] text-[#FBF8F5] text-sm font-semibold border border-[#D4AF37]/30 disabled:opacity-60">
          <Ban className="w-4 h-4" /> {endDate && endDate > date ? "Segna periodo come chiuso" : "Segna come chiuso"}
        </button>
      </form>
      <div className="lg:col-span-3 min-w-0 space-y-3">
        {closures.length === 0 && <p className="text-sm text-[#6E6865]">Nessuna chiusura straordinaria.</p>}
        {closures.map((c) => (
          <div key={c.id} data-testid={`closure-row-${c.id}`} className="flex items-center gap-4 p-4 rounded-xl border border-[#D4AF37]/12 bg-[#141217]">
            <Ban className="w-5 h-5 text-[#941B30] shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-[#FBF8F5] capitalize">{fmt(c.date)}</p>
              {c.reason && <p className="text-xs text-[#6E6865] truncate">{c.reason}</p>}
            </div>
            <button onClick={() => del(c.id)} data-testid={`closure-delete-${c.id}`} className="p-2 text-[#A39E9B] hover:text-[#941B30]"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

const inputCls = "w-full px-3 py-2.5 rounded-lg bg-[#0B0A0C] border border-[#D4AF37]/20 text-[#FBF8F5] focus:border-[#D4AF37] outline-none text-sm";
const EMPTY = { author: "", text: "", rating: 5, category: "club", visible: true };

export function ReviewsManager({ refresh }) {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => { const { data } = await api.get("/reviews?all=true"); setList(data); };
  useEffect(() => { load(); }, []);

  const reset = () => { setForm(EMPTY); setEditId(null); };
  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editId) await api.put(`/reviews/${editId}`, form); else await api.post("/reviews", form);
      toast.success(editId ? "Recensione aggiornata" : "Recensione aggiunta");
      reset(); await load(); await refresh();
    } catch { toast.error("Errore nel salvataggio"); }
    finally { setSaving(false); }
  };
  const del = async (id) => { if (!window.confirm("Eliminare questa recensione?")) return; await api.delete(`/reviews/${id}`); toast.success("Eliminata"); await load(); await refresh(); };
  const toggle = async (r) => { await api.put(`/reviews/${r.id}`, { ...r, visible: !r.visible }); await load(); await refresh(); };
  const edit = (r) => { setEditId(r.id); setForm({ author: r.author, text: r.text, rating: r.rating, category: r.category, visible: r.visible }); };

  return (
    <div className="grid lg:grid-cols-5 gap-8 min-w-0">
      <form onSubmit={save} className="lg:col-span-2 min-w-0 space-y-4 p-5 sm:p-6 rounded-2xl border border-[#D4AF37]/15 bg-[#141217] h-fit">
        <h3 className="font-display text-xl text-[#FBF8F5]">{editId ? "Modifica Recensione" : "Nuova Recensione"}</h3>
        <div>
          <label className="text-xs text-[#A39E9B] mb-1 block">Nome ospite</label>
          <input required value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} data-testid="review-author-input" className={inputCls} />
        </div>
        <div>
          <label className="text-xs text-[#A39E9B] mb-1 block">Testimonianza</label>
          <textarea required rows={4} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} data-testid="review-text-input" className={inputCls} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-[#A39E9B] mb-1 block">Sezione</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="review-category-select" className={inputCls}>
              <option value="club">Club Privé</option><option value="spa">Spa Naturista</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-[#A39E9B] mb-1 block">Stelle</label>
            <div className="flex gap-1 py-2" data-testid="review-rating">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" onClick={() => setForm({ ...form, rating: n })}>
                  <Star className="w-5 h-5 text-[#D4AF37]" fill={n <= form.rating ? "#D4AF37" : "transparent"} />
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex gap-3">
          <button type="submit" disabled={saving} data-testid="review-save-btn"
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-full bg-[#7A1426] hover:bg-[#941B30] text-[#FBF8F5] text-sm font-semibold border border-[#D4AF37]/30 disabled:opacity-60">
            <Plus className="w-4 h-4" /> {editId ? "Aggiorna" : "Aggiungi"}
          </button>
          {editId && <button type="button" onClick={reset} className="px-5 py-3 rounded-full border border-[#D4AF37]/20 text-[#A39E9B] text-sm">Annulla</button>}
        </div>
      </form>

      <div className="lg:col-span-3 min-w-0 space-y-3">
        {list.length === 0 && <p className="text-sm text-[#6E6865]">Nessuna recensione.</p>}
        {list.map((r) => (
          <div key={r.id} data-testid={`review-row-${r.id}`} className={`p-4 rounded-xl border border-[#D4AF37]/12 bg-[#141217] ${r.visible ? "" : "opacity-50"}`}>
            <div className="flex items-center gap-2 mb-1">
              <span className="font-display text-lg text-[#FBF8F5]">{r.author}</span>
              <span className={`text-[0.6rem] px-2 py-0.5 rounded-full ${r.category === "spa" ? "bg-[#C59B6D]/20 text-[#C59B6D]" : "bg-[#7A1426]/30 text-[#F3E5AB]"}`}>{r.category === "spa" ? "Spa" : "Club"}</span>
              <span className="text-xs text-[#D4AF37] ml-1">{"★".repeat(r.rating)}</span>
              <div className="ml-auto flex">
                <button onClick={() => toggle(r)} data-testid={`review-toggle-${r.id}`} title={r.visible ? "Nascondi" : "Mostra"} className="p-2 text-[#A39E9B] hover:text-[#D4AF37]">{r.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</button>
                <button onClick={() => edit(r)} className="p-2 text-[#A39E9B] hover:text-[#D4AF37]"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => del(r.id)} data-testid={`review-delete-${r.id}`} className="p-2 text-[#A39E9B] hover:text-[#941B30]"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <p className="text-sm text-[#A39E9B] italic">“{r.text}”</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function NewsletterManager() {
  const [list, setList] = useState([]);
  const load = async () => { const { data } = await api.get("/newsletter"); setList(data); };
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (!window.confirm("Rimuovere questo iscritto?")) return; await api.delete(`/newsletter/${id}`); toast.success("Rimosso"); await load(); };
  const exportCsv = async () => {
    const res = await api.get("/newsletter/export", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = "iscritti_afrodite.csv"; a.click();
    URL.revokeObjectURL(url);
  };
  const fmt = (d) => new Date(d).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });

  return (
    <div className="max-w-3xl">
      <div className="flex items-center gap-4 mb-6 p-5 rounded-2xl border border-[#D4AF37]/15 bg-[#141217]">
        <Mail className="w-5 h-5 text-[#D4AF37]" />
        <p className="text-sm text-[#A39E9B]"><span className="text-[#F3E5AB] font-semibold" data-testid="newsletter-count">{list.length}</span> soci iscritti alla newsletter</p>
        <button onClick={exportCsv} disabled={list.length === 0} data-testid="newsletter-export-btn"
          className="ml-auto flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#D4AF37] text-black text-sm font-semibold hover:bg-[#F3E5AB] disabled:opacity-40 transition-colors">
          <Download className="w-4 h-4" /> Esporta CSV
        </button>
      </div>
      <div className="space-y-2">
        {list.length === 0 && <p className="text-sm text-[#6E6865]">Nessun iscritto per ora.</p>}
        {list.map((s) => (
          <div key={s.id} data-testid={`subscriber-row-${s.id}`} className="flex items-center gap-4 px-4 py-3 rounded-xl border border-[#D4AF37]/12 bg-[#141217]">
            <span className="text-sm text-[#FBF8F5] flex-1 truncate">{s.email}</span>
            <span className="text-xs text-[#6E6865]">{fmt(s.created_at)}</span>
            <button onClick={() => del(s.id)} data-testid={`subscriber-delete-${s.id}`} className="p-2 text-[#A39E9B] hover:text-[#941B30]"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
